import {
  STICKER_REGISTRY,
  type WalletSticker,
} from "@/components/wallet/wallet-stickers";

/**
 * Sharing a Hiive sticker on a post or a comment. Pure, so the composer, the
 * feed card and the server all agree on what a shared sticker is.
 *
 * What is stored is only the sticker id. Its owner is always the author of
 * the post or comment it rides on, and the server refuses an attachment the
 * author has not unlocked (`src/lib/wallet/member-stickers.ts`), so a shared
 * sticker is always one its author really holds.
 */

export interface SharedStickerRef {
  stickerId: string;
}

const BY_ID = new Map(STICKER_REGISTRY.map((sticker) => [sticker.id, sticker]));

export function findSticker(
  stickerId: string | null | undefined,
): WalletSticker | null {
  return stickerId ? (BY_ID.get(stickerId) ?? null) : null;
}

/**
 * The integer a sticker is stored as in `post_context_tag.context_id`: FNV-1a
 * of its id, kept to 31 bits. Derived from the id rather than the registry
 * position so reordering or growing the registry never re-points an old post.
 * `sticker-share.test.ts` proves the registry has no collisions.
 */
export function stickerContextId(stickerId: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < stickerId.length; index += 1) {
    hash ^= stickerId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) & 0x7fffffff;
}

const BY_CONTEXT_ID = new Map(
  STICKER_REGISTRY.map((sticker) => [stickerContextId(sticker.id), sticker]),
);

export function stickerFromContextId(contextId: number): WalletSticker | null {
  return BY_CONTEXT_ID.get(contextId) ?? null;
}

/** One line for notifications, transcripts and the bot: "Hiive sticker: Champion". */
export function sharedStickerLabel(stickerId: string): string {
  const sticker = findSticker(stickerId);
  return sticker ? `Hiive sticker: ${sticker.title}` : "Hiive sticker";
}
